/**
 * Message Component - Alert/Toast Notification Display
 * 
 * Displays temporary alert messages with automatic dismissal and manual close functionality
 * Supports multiple alert types with contextual styling and icons
 * 
 * Features:
 * - Auto-dismiss after configurable timeout (default: 10 seconds)
 * - Multiple alert types: success, error, info
 * - Type-specific icons and styling from Bootstrap alert classes
 * - Manual close button for immediate dismissal
 * - Uncontrolled visibility state
 * - Returns null when not visible (no DOM footprint)
 * 
 * Alert Types:
 * - success: Green alert with CircleCheckBig icon
 * - error: Red alert with CircleX icon  
 * - info: Blue alert with Info icon (default)
 * 
 * Styling:
 * - Uses Bootstrap alert classes for consistent styling
 * - CSS custom properties for alert colors (--vz-alert-color)
 * - Custom class 'custom-toast' for additional styling
 * - Close button positioned absolutely in top-right corner
 * 
 * Dependencies:
 * - React hooks: useEffect, useState
 * - CommonIcon: Icon component for alert icons
 * - Bootstrap CSS for alert styling
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.message - Message text to display
 * @param {string} [props.type='info'] - Alert type: 'success', 'error', or 'info'
 * 
 * @returns {JSX.Element|null} Alert element with icon and message, or null if not visible
 * 
 * @example
 * // Success message
 * <Message message="Data saved successfully!" type="success" />
 * 
 * @example
 * // Error message
 * <Message message="Failed to save data" type="error" />
 * 
 * @example
 * // Info message (default type)
 * <Message message="Please review the changes" />
 * 
 * @example
 * // In a form submission
 * {showMessage && <Message message="Form submitted" type="success" />}
 */

import React, { useEffect, useState } from 'react';
import CommonIcon from './customicon';

/**
 * Message Component
 * Alert notification that auto-dismisses after timeout
 * 
 * @param {Object} props
 * @param {string} props.message - Alert message text
 * @param {string} [props.type='info'] - Alert type (success, error, info)
 * @returns {JSX.Element|null} Alert element or null
 */
const Message = ({ message, type = 'info' }) => {
  /**
   * Control visibility of the alert
   * When false, component returns null and removes from DOM
   */
  const [visible, setVisible] = useState(true);

  /**
   * Auto-dismiss alert after timeout
   * Current timeout: 10 seconds (10000ms)
   * 
   * Process:
   * 1. Starts timer on component mount
   * 2. Sets visible to false after timeout
   * 3. Cleans up timer on component unmount
   */
  useEffect(() => {
    const timer = setTimeout(() => {
       setVisible(false);
    }, 10000); // 10 seconds auto-dismiss
    return () => clearTimeout(timer); // Cleanup the timer on unmount
  }, []);

  /**
   * Determine icon and styling based on alert type
   * 
   * Type mapping:
   * - success: Green alert + CircleCheckBig icon
   * - error: Red alert + CircleX icon
   * - info: Blue alert + Info icon (default)
   * 
   * @type {JSX.Element} icon - Icon component to display
   * @type {string} alertClass - Bootstrap alert class name
   */
  let icon, alertClass;
  switch (type) {
    case 'success':
      icon = <CommonIcon iconName="CircleCheckBig" size={"16px"} color="var(--vz-alert-color)" />;
      alertClass = 'alert alert-success';
      break;
    case 'error':
      icon = <CommonIcon iconName="CircleX" size={"16px"} color="var(--vz-alert-color)" />;
      alertClass = 'alert alert-danger';
      break;
    case 'info':
    default:
      icon = <CommonIcon iconName="Info" size={"16px"} color="var(--vz-alert-color)" />;
      alertClass = 'alert alert-info';
      break;
  }

  /**
   * Handle manual close button click
   * Sets visible to false, triggering component removal
   */
  const handleClose = () => {
    setVisible(false);
  };

  /**
   * Return null if alert is not visible
   * Removes alert from DOM completely (no hidden elements)
   */
  if (!visible) {
    return null;
  }

  return (
    <div className={alertClass+" custom-toast"} role="alert" >
      {/* 
        Close button - positioned absolutely in top-right corner
        - Styled as transparent button with no border
        - Uses CircleX icon
        - Click handler sets visible to false
      */}
      <button
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: "-16px",
          right: "-9px",
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--vz-alert-color)',
          fontSize: '20px'
        }}
      >
        <CommonIcon iconName="CircleX" size={"20px"} color="#000" />
      </button>

      {/* Alert content: icon and message text */}
      {icon} {message || 'This is a success alert—check it out!'}
    </div>
  );
};

export default Message;
